import {
    BOARD_SIZE,
    pieceVisualCellOffsetPx,
    pieceVisualRenderSizePx,
} from "../../mobile-jigsaw/board/PieceVisual";

/** Smooth step for drag-like motion (ease in-out). */
export function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;
}

/** Quadratic Bézier point at t ∈ [0, 1]. */
export function quadBezier(t, p0, p1, p2) {
    const u = 1 - t;
    return u * u * p0 + 2 * u * t * p1 + t * t * p2;
}

/**
 * Screen-space rect for a piece snapped at its home cell on the board.
 * @param {number} pieceIndex
 * @param {number} grid
 * @param {HTMLElement | null} boardInnerEl — the 500×500 board layer
 */
export function boardHomeScreenRect(pieceIndex, grid, boardInnerEl) {
    if (!boardInnerEl || grid <= 0) return null;

    const boardRect = boardInnerEl.getBoundingClientRect();
    if (boardRect.width <= 0) return null;

    const displayScale = boardRect.width / BOARD_SIZE;
    const cellPx = BOARD_SIZE / grid;
    const col = pieceIndex % grid;
    const row = Math.floor(pieceIndex / grid);
    const x = col * cellPx;
    const y = row * cellPx;
    const offset = pieceVisualCellOffsetPx(cellPx);
    const renderSize = pieceVisualRenderSizePx(cellPx);
    const size = renderSize * displayScale;

    return {
        left: boardRect.left + (x - offset) * displayScale,
        top: boardRect.top + (y - offset) * displayScale,
        width: size,
        height: size,
    };
}

/** Curved control point between tray and board (arc above the chord). */
export function flyControlPoint(fromRect, toRect) {
    const midX = (fromRect.left + toRect.left) / 2;
    const midY = (fromRect.top + toRect.top) / 2;
    const span = Math.hypot(toRect.left - fromRect.left, toRect.top - fromRect.top);
    const lift = Math.max(48, span * 0.22);
    const side = (fromRect.left - toRect.left) * 0.08;
    return {
        x: midX + side,
        y: midY - lift,
    };
}

/**
 * Animate one piece along a quadratic curve in screen space.
 * When `constantSize` is true, width/height stay at the tray size for the whole flight.
 */
export function animatePieceAlongCurve({
    fromRect,
    toRect,
    control,
    durationMs = 920,
    constantSize = false,
    onFrame,
    onDone,
}) {
    const start = performance.now();
    let rafId = 0;

    const tick = (now) => {
        const linear = Math.min(1, (now - start) / durationMs);
        const t = easeInOutCubic(linear);

        const left = quadBezier(t, fromRect.left, control.x, toRect.left);
        const top = quadBezier(t, fromRect.top, control.y, toRect.top);
        const width = constantSize ? fromRect.width : fromRect.width + (toRect.width - fromRect.width) * t;
        const height = constantSize ? fromRect.height : fromRect.height + (toRect.height - fromRect.height) * t;

        onFrame?.({ left, top, width, height, t });

        if (linear < 1) {
            rafId = requestAnimationFrame(tick);
        } else {
            onDone?.();
        }
    };

    rafId = requestAnimationFrame(tick);

    return () => {
        if (rafId) cancelAnimationFrame(rafId);
    };
}
